import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import useExamProtection from '../../utils/useExamProtection';
import { useAuth } from '../contexts/AuthContext';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { formatCustomTagsToLaTeX } from '../common/QuestionFormatter';
import MathTextRenderer from '../common/MathTextRenderer';

// Validation function for quiz data structure
const validateQuizData = (quizData) => {
  if (!quizData || !quizData.questions || !Array.isArray(quizData.questions)) {
    return false;
  }

  return quizData.questions.every(question => {
    return (
      typeof question.question === 'string' &&
      Array.isArray(question.options) &&
      question.options.length >= 2 &&
      question.options.every(option => typeof option === 'string') &&
      typeof question.correctAnswer === 'number' &&
      question.correctAnswer >= 0 &&
      question.correctAnswer < question.options.length
    );
  });
};

// MathJax configuration
const config = {
  loader: { load: ["[tex]/html"] },
  tex: {
    packages: { "[+]": ["html"] },
    inlineMath: [["$", "$"], ["\\(", "\\)"]],
    displayMath: [["$$", "$$"], ["\\[", "\\]"]],
    processEscapes: true,
    macros: {
      '\\ce': '{\\text{#1}}'
    }
  },
  options: {
    enableMenu: false,
  },
};

function ExamPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentSubCourse, setCurrentSubCourse] = useState(null);
  const [error, setError] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [violationMessage, setViolationMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitReason, setSubmitReason] = useState('manual'); // manual | time | violations | error
  const [attemptsSoFar, setAttemptsSoFar] = useState(0);
  

  // Fetch quiz data and check attempt status
  useEffect(() => {
    const fetchQuizAndCheckAttempt = async () => {
      try {
        if (!user) {
          setError('Please log in to take the exam');
          setLoading(false);
          return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const moduleId = searchParams.get('module');
        const subCourseId = searchParams.get('subcourse');

        if (!moduleId || !subCourseId) {
          setError('Invalid exam parameters');
          setLoading(false);
          return;
        }

        const courseRef = doc(db, 'courses', courseId);
        const courseDoc = await getDoc(courseRef);

        if (!courseDoc.exists()) {
          setError('Course not found');
          setLoading(false);
          return;
        }

        const courseData = courseDoc.data();
        const module = courseData.modules?.find(m => m.moduleId === moduleId);
        const subCourse = module?.subCourses?.find(sc => sc.subCourseId === subCourseId);

        if (!subCourse?.quizData) {
          setError('Quiz not found');
          setLoading(false);
          return;
        }

        // Validate quiz data structure
        if (!validateQuizData(subCourse.quizData)) {
          console.error('Invalid quiz data structure:', subCourse.quizData);
          setError('Invalid quiz format. Please contact support.');
          setLoading(false);
          return;
        }

        // Check previous attempts
        const resultId = `${courseId}_${moduleId}_${subCourseId}`;
        const attemptRef = doc(db, 'users', user.uid, 'examResults', resultId);
        const attemptDoc = await getDoc(attemptRef);

        if (attemptDoc.exists()) {
          const resultData = attemptDoc.data();
          const attempts = resultData.attempts || 0;
          const score = resultData.score || 0;
          setAttemptsSoFar(attempts);
          if (attempts >= 3 || score >= 75) {
            navigate(`/courses/${courseId}/results?module=${moduleId}&subcourse=${subCourseId}`);
            return;
          }
        }

        setQuiz(subCourse.quizData);
        setCurrentModule(module);
        setCurrentSubCourse(subCourse);
        setTimeLeft((subCourse.quizData.questions.length || 0) * 30);
        setLoading(false);

      } catch (error) {
        console.error('Error fetching quiz:', error);
        setError('Failed to load exam. Please try again.');
        setLoading(false);
      }
    };

    fetchQuizAndCheckAttempt();
  }, [courseId, user, navigate]);

  // Restore draft answers/time if available
  useEffect(() => {
    const restoreDraft = async () => {
      try {
        if (!user || !quiz || !currentModule || !currentSubCourse) return;
        const resultId = `${courseId}_${currentModule.moduleId}_${currentSubCourse.subCourseId}`;
        const draftRef = doc(db, 'users', user.uid, 'examDrafts', resultId);
        const draftSnap = await getDoc(draftRef);
        if (draftSnap.exists()) {
          const draft = draftSnap.data();
          if (draft && draft.answers) {
            // Ensure numeric answers
            const formatted = {};
            Object.keys(draft.answers || {}).forEach(k => {
              formatted[k] = Number(draft.answers[k]);
            });
            setAnswers(formatted);
          }
          if (typeof draft.timeLeft === 'number' && draft.timeLeft > 0) {
            setTimeLeft(draft.timeLeft);
          }
        }
      } catch (e) {
        console.warn('Failed to restore exam draft:', e);
      }
    };
    restoreDraft();
  }, [user, quiz, currentModule, currentSubCourse, courseId]);

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    console.log('Selected:', questionIndex, answerIndex);
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const submitExam = useCallback(async () => {
    console.log('Submit exam called');
    if (!quiz || !user || !currentModule || !currentSubCourse) {
      console.log('Missing data for submission:', { quiz, user, currentModule, currentSubCourse });
      setError('Cannot submit exam due to missing data.');
      // Show modal with error reason so user understands why
      setSubmitReason('error');
      setShowConfirmModal(true);
      return;
    }

    const results = {
      totalQuestions: quiz.questions.length,
      correctAnswers: 0,
      answers: answers,
      timestamp: serverTimestamp(),
      courseId: courseId,
      moduleId: currentModule.moduleId,
      subCourseId: currentSubCourse.subCourseId,
      timeTaken: (quiz.questions.length || 0) * 30 - timeLeft
    };

    // Calculate score
    quiz.questions.forEach((question, index) => {
      if (Number(answers[index]) === Number(question.correctAnswer)) {
        results.correctAnswers += 1;
      }
    });

    results.score = (results.correctAnswers / results.totalQuestions) * 100;

    try {
      // Ensure answers are saved correctly in the Firestore document
      // Convert answers object to a properly formatted object with numeric indices
      let formattedAnswers = {};
      Object.keys(answers).forEach(key => {
        formattedAnswers[key] = Number(answers[key]);
      });
      
      // Replace the answers with the formatted version
      results.answers = formattedAnswers;
      
      // Save results with a unique ID combining course, module, and subcourse
      const resultId = `${courseId}_${currentModule.moduleId}_${currentSubCourse.subCourseId}`;
      console.log('Saving results with formatted answers:', results);
      console.log('Formatted answers object:', formattedAnswers);
      
      // Reference to the user's specific exam result document
      const userExamResultRef = doc(db, 'users', user.uid, 'examResults', resultId);
      
      // Get current attempt data to increment attempts count
      const attemptDoc = await getDoc(userExamResultRef);
      const currentAttempts = attemptDoc.exists() ? attemptDoc.data().attempts || 0 : 0;
      
      // Save to user's examResults collection
      await setDoc(userExamResultRef, {
        ...results,
        userId: user.uid, // for security rules
        attempts: currentAttempts + 1 // Increment attempt count
      }, { merge: true }); // Use merge to update existing document without overwriting other fields

      // If the user passes, mark the sub-course as completed
      if (results.score >= 75) {
        // Reference to the user's completed sub-courses document
        const completedRef = doc(db, 'users', user.uid, 'completedSubCourses', courseId);
        
        // Use a transaction to ensure atomicity when updating nested map
        await updateDoc(completedRef, {
          [`completed.${resultId}`]: true // Use bracket notation for dynamic field
        }, { merge: true });
      }

      // Navigate to results page with module and subcourse information
      navigate(`/courses/${courseId}/results?module=${currentModule.moduleId}&subcourse=${currentSubCourse.subCourseId}`);
    } catch (error) {
      console.error('Error saving exam results:', error);
      setError('Failed to save exam results. Please try again.');
      // Offer user a chance to confirm submission again in case of transient errors
      setSubmitReason('error');
      setShowConfirmModal(true);
    }
  }, [answers, quiz, user, courseId, currentModule, currentSubCourse, timeLeft, navigate]);

  const requestSubmit = useCallback((reason = 'manual') => {
    setSubmitReason(reason);
    setShowConfirmModal(true);
  }, []);

  // Hook: exam protection (copy/paste/drag, visibility, autosave)
  useExamProtection({
    enabled: !!user && !!quiz,
    getState: () => ({ answers, timeLeft }),
    saveDraft: async ({ answers: a, timeLeft: t }) => {
      if (!user || !currentModule || !currentSubCourse) return;
      try {
        const resultId = `${courseId}_${currentModule.moduleId}_${currentSubCourse.subCourseId}`;
        const draftRef = doc(db, 'users', user.uid, 'examDrafts', resultId);
        const formatted = {};
        Object.keys(a || {}).forEach(k => { formatted[k] = Number(a[k]); });
        await setDoc(draftRef, {
          answers: formatted,
          timeLeft: Number(t) || 0,
          updatedAt: new Date().toISOString(),
          courseId,
          moduleId: currentModule.moduleId,
          subCourseId: currentSubCourse.subCourseId
        }, { merge: true });
      } catch (e) {
        // best-effort: ignore autosave failures
      }
    },
    onViolation: (type, count) => {
      setViolationCount(count);
      setViolationMessage('Exam tab lost focus or hidden. Please stay on this page.');
    },
    onAutoSubmit: () => {
      // Prompt before submitting due to repeated violations
      requestSubmit('violations');
    },
    autosaveMs: 15000,
    maxViolationsBeforeAutoSubmit: 4,
    warnBeforeUnload: true,
    countOnBlur: false, // do not count transient blur alone
    violationDebounceMs: 30000, // require 30s sustained hidden/blur
    violationThrottleMs: 10000, // at most one violation every 10s
  });

  // Timer logic
  useEffect(() => {
    if (!timeLeft || !quiz || loading) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Prompt before submitting due to time up
          requestSubmit('time');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, quiz, loading, submitExam]);

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(130,88,18)]"></div></div>;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return <div className="flex justify-center items-center min-h-screen"><div>No quiz questions found for this course.</div></div>;
  }

  return (
    <MathJaxContext config={config}>
    <div className="max-w-3xl mx-auto p-6 select-none">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Course Examination</h1>
          {timeLeft > 0 && (
        <div className="text-xl font-semibold">
          Time Left: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
        </div>
          )}
      </div>

      {violationCount > 0 && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {violationMessage} (Warnings: {violationCount}/3)
        </div>
      )}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold mb-2">Confirm Submission</h3>
            <p className="text-sm text-gray-700 mb-4">
              {submitReason === 'manual' && 'Are you sure you want to submit your answers now?'}
              {submitReason === 'time' && 'Time is up. Please confirm to submit your answers.'}
              {submitReason === 'violations' && 'Multiple focus/visibility violations detected. Please confirm to submit your answers.'}
              {submitReason === 'error' && 'An unexpected error occurred. Please confirm to submit your answers now.'}
            </p>
            <div className="text-sm text-gray-600 mb-4">
              Attempts used: {attemptsSoFar}/3{attemptsSoFar < 3 ? ` — You have ${3 - attemptsSoFar} retake(s) if you score below 75%.` : ''}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  submitExam();
                }}
                className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracker UI */}
      {quiz && quiz.questions && (
        <div className="flex justify-center gap-8 mb-6">
          <div className="bg-gray-100 rounded px-4 py-2 text-center">
            <div className="text-xs text-gray-500">Answered</div>
            <div className="font-bold text-lg">{Object.keys(answers).length}</div>
          </div>
          <div className="bg-gray-100 rounded px-4 py-2 text-center">
            <div className="text-xs text-gray-500">Skipped</div>
            <div className="font-bold text-lg">{quiz.questions.length - Object.keys(answers).length}</div>
          </div>
          <div className="bg-gray-100 rounded px-4 py-2 text-center">
            <div className="text-xs text-gray-500">Total</div>
            <div className="font-bold text-lg">{quiz.questions.length}</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        {quiz.questions[currentQuestion] && (
          <>
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-2">
                Question {currentQuestion + 1} of {quiz.questions.length}
              </h2>
                <MathTextRenderer text={formatCustomTagsToLaTeX(quiz.questions[currentQuestion].question)} />
            </div>

              <div className="space-y-2"
                 key={currentQuestion}
              >
              {quiz.questions[currentQuestion].options.map((option, index) => (
                <label
                  key={index}
                  className={`block p-3 border rounded-lg cursor-pointer transition-colors
                    ${answers[currentQuestion] === index ? 'bg-[rgb(130,88,18)] text-white' : 'hover:bg-gray-50'}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion}`}
                    value={index}
                    checked={answers[currentQuestion] === index}
                    onChange={() => handleAnswerSelect(currentQuestion, index)}
                    className="hidden"
                  />
                    <MathTextRenderer text={formatCustomTagsToLaTeX(option)} />
                </label>
              ))}
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setCurrentQuestion(prev => prev - 1)}
                disabled={currentQuestion === 0}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                Previous
              </button>

              {currentQuestion < quiz.questions.length - 1 ? (
                <button
                  onClick={() => setCurrentQuestion(prev => prev + 1)}
                  className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => {
                    console.log('Submit button clicked');
                    requestSubmit('manual');
                  }}
                  className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded hover:bg-[rgb(110,68,0)]"
                >
                  Submit Exam
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
    </MathJaxContext>
  );
}

export default ExamPage; 